import { useState } from 'react';
import { Download, Maximize2, Trash2 } from 'lucide-react';
import { useGalleryImageDataQuery } from '../../features/gallery/queries/useGalleryImageDataQuery';
import { useDeleteGalleryImageMutation } from '../../features/gallery/mutations/useGalleryImageMutations';
import { LiksChatMessageImagePreviewModal } from '../../components/liks/LiksChatMessage/LiksChatMessageImagePreviewModal';
import { LiksChatMessageImageActionButton } from '../../components/liks/LiksChatMessage/LiksChatMessageImageActionButton';
import { LiksChatMessageImageMetaBadges } from '../../components/liks/LiksChatMessage/LiksChatMessageImageMetaBadges';
import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import { useBlobObjectUrl } from '../../hooks/useBlobObjectUrl';
import { useElementVisibility } from '../../hooks/useElementVisibility';
import type { GalleryImageWithIdentity } from '../../types/gallery';
import '../../components/liks/LiksChatMessage/LiksChatMessage.css';

function resolveImageAlt(
  image: GalleryImageWithIdentity,
  fallbackAlt: string,
): string {
  const title = image.title?.trim();
  if (title) {
    return title;
  }

  const fileName = image.fileName?.trim();
  if (fileName) {
    return fileName;
  }

  const prompt = image.promptSnapshot?.trim();
  if (prompt) {
    return prompt;
  }

  return fallbackAlt;
}

export type GalleryImageCardProps = {
  image: GalleryImageWithIdentity;
  downloadLabel: string;
  expandLabel: string;
  deleteLabel: string;
  deleteConfirmTitle: string;
  deleteConfirmMessage: string;
  deleteConfirmAction: string;
  deleteCancelAction: string;
  closePreviewLabel: string;
  loadingLabel: string;
  imageAltFallback: string;
};

export function GalleryImageCard({
  image,
  downloadLabel,
  expandLabel,
  deleteLabel,
  deleteConfirmTitle,
  deleteConfirmMessage,
  deleteConfirmAction,
  deleteCancelAction,
  closePreviewLabel,
  loadingLabel,
  imageAltFallback,
}: GalleryImageCardProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deleteImageMutation = useDeleteGalleryImageMutation();
  const { ref: visibilityRef, hasBeenVisible } = useElementVisibility();
  const imageDataQuery = useGalleryImageDataQuery(image.id, {
    enabled: hasBeenVisible,
  });
  const objectUrl = useBlobObjectUrl(image.id, imageDataQuery.data);
  const alt = resolveImageAlt(image, imageAltFallback);

  const handleDownload = () => {
    if (!objectUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = image.fileName?.trim() || 'gallery-image';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const showLoading =
    !hasBeenVisible ||
    imageDataQuery.isLoading ||
    (imageDataQuery.isSuccess && objectUrl == null);
  const showError = hasBeenVisible && imageDataQuery.isError;

  if (showError) {
    return null;
  }

  return (
    <>
      <div
        ref={visibilityRef}
        className={[
          'gallery-page__image-card',
          'liks-chat-message__image-frame',
          showLoading && 'liks-chat-message__image-frame--loading',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-busy={showLoading && hasBeenVisible ? true : undefined}
        aria-label={showLoading && hasBeenVisible ? loadingLabel : undefined}
      >
        {showLoading ? (
          hasBeenVisible ? (
            <div className="liks-chat-message__spinner" aria-hidden />
          ) : null
        ) : (
          <>
            <img
              className="liks-chat-message__image"
              src={objectUrl!}
              alt={alt}
              loading="lazy"
            />
            <div className="liks-chat-message__image-actions">
              <LiksChatMessageImageActionButton
                label={deleteLabel}
                className="liks-chat-message__image-action liks-chat-message__image-action--danger"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2
                  className="liks-chat-message__image-action-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksChatMessageImageActionButton>
              <LiksChatMessageImageActionButton
                label={downloadLabel}
                onClick={handleDownload}
              >
                <Download
                  className="liks-chat-message__image-action-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksChatMessageImageActionButton>
              <LiksChatMessageImageActionButton
                label={expandLabel}
                onClick={() => setIsPreviewOpen(true)}
              >
                <Maximize2
                  className="liks-chat-message__image-action-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksChatMessageImageActionButton>
            </div>
            <LiksChatMessageImageMetaBadges
              imageFormat={image.imageFormat}
              imageAspectRatio={image.imageAspectRatio}
              variant="overlay"
            />
          </>
        )}
      </div>

      <LiksChatMessageImagePreviewModal
        isOpen={isPreviewOpen && objectUrl != null}
        imageUrl={objectUrl ?? ''}
        alt={alt}
        title={image.title}
        imageFormat={image.imageFormat}
        imageAspectRatio={image.imageAspectRatio}
        downloadLabel={downloadLabel}
        onDownload={handleDownload}
        closeLabel={closePreviewLabel}
        onClose={() => setIsPreviewOpen(false)}
      />

      <LiksConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={deleteConfirmTitle}
        message={deleteConfirmMessage}
        confirmLabel={deleteConfirmAction}
        cancelLabel={deleteCancelAction}
        isBusy={deleteImageMutation.isPending}
        onConfirm={() => {
          if (deleteImageMutation.isPending) {
            return;
          }
          deleteImageMutation.mutate(image.id, {
            onSuccess: () => {
              setIsDeleteDialogOpen(false);
            },
          });
        }}
        onCancel={() => {
          if (!deleteImageMutation.isPending) {
            setIsDeleteDialogOpen(false);
          }
        }}
      />
    </>
  );
}
